import axios from 'axios'
import { showRateLimitNotice } from './rateLimitNotice.js'

const API_BASE_URL = import.meta.env?.VITE_API_BASE ?? '/api'
const TOKEN_STORAGE_KEY = 'token'
const unauthorizedHandlers = new Set()

const api = axios.create({
  baseURL: API_BASE_URL,
})

api.interceptors.request.use((config) => {
  const token = getAuthToken()
  if (token) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 429) {
      showRateLimitNotice(error.response?.data?.message)
    }

    if (isAccountUnavailableError(error)) {
      clearAuthToken()
      notifyUnauthorized(error)
    }

    if (error.response?.status === 401) {
      // Check if this is a token-related error (should logout) vs business logic error (should not logout)
      const errorData = error.response?.data || {}
      const errorMessage = (errorData.error || errorData.message || '').toLowerCase()
      
      // Token-related error patterns that should trigger logout
      const tokenErrorPatterns = [
        'access token required',
        'invalid token',
        'token expired',
        'authentication token',
        'token is not valid',
        'token missing',
        'please provide a valid authentication token'
      ]
      
      const isTokenError = tokenErrorPatterns.some(pattern => 
        errorMessage.includes(pattern)
      )
      
      // Only logout for actual token expiration/invalid token errors
      // Business logic 401 errors (like "Invalid old password") should not trigger logout
      if (isTokenError) {
        clearAuthToken()
        notifyUnauthorized(error)
      }
    }
    return Promise.reject(error)
  }
)

export const isAccountUnavailableError = (error) => {
  const status = error?.response?.status
  const errorCode = String(error?.response?.data?.error || '').toUpperCase()
  const message = String(error?.response?.data?.message || '').toLowerCase()
  return status === 403 && (
    errorCode === 'ACCOUNT_NOT_AVAILABLE'
    || message.includes('account not available')
  )
}

export function getAuthToken() {
  return window.localStorage.getItem(TOKEN_STORAGE_KEY)
}

export function setAuthToken(token) {
  if (token) {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token)
  } else {
    clearAuthToken()
  }
}

export function clearAuthToken() {
  window.localStorage.removeItem(TOKEN_STORAGE_KEY)
}

export function onUnauthorized(callback) {
  unauthorizedHandlers.add(callback)
  return () => unauthorizedHandlers.delete(callback)
}

function notifyUnauthorized(error) {
  unauthorizedHandlers.forEach((callback) => {
    try {
      callback(error)
    } catch (handlerError) {
      console.error('Unauthorized handler failed', handlerError)
    }
  })
}

/**
 * Initialize a payment with Paystack
 * @param {Object} data - Payment initialization data
 * @param {string} data.type - Payment type ('PLAN' or 'TOPUP')
 * @param {string} data.planId - Plan ID (required for PLAN type)
 * @param {string} data.planType - Plan type ('individual' or 'family')
 * @param {number} data.amount - Amount in naira (required for TOPUP type)
 * @returns {Promise<Object>} Payment initialization response with payment_url
 */
export const initializePayment = async (data) => {
  try {
    const response = await api.post('/payments/initialize', data);
    return response.data;
  } catch (error) {
    console.error('Payment initialization error:', error);
    throw error;
  }
};

/**
 * Renew subscription with Paystack (redirect to payment_url)
 * @param {string} subscriptionId - Subscription ID to renew
 * @returns {Promise<Object>} { payment_url, reference, amount }
 */
export const renewWithPaystack = async (subscriptionId) => {
  const response = await api.post(`/subscriptions/${subscriptionId}/renew`);
  return response.data;
};

/**
 * Renew subscription using wallet balance
 * @param {string} subscriptionId - Subscription ID to renew
 * @returns {Promise<Object>} { success, reference, amount }
 */
export const renewWithWallet = async (subscriptionId) => {
  const response = await api.post('/payments/wallet/renew', { subscriptionId });
  return response.data;
};

/**
 * Upgrade to a new plan with Paystack (redirect to payment_url). Subscription must be EXPIRED.
 * @param {string} subscriptionId - Current (expired) subscription ID
 * @param {string} planId - New plan ID
 * @param {string} [planType] - 'individual' or 'family'
 * @returns {Promise<Object>} { payment_url, reference, amount }
 */
export const upgradeWithPaystack = async (subscriptionId, planId, planType = 'individual') => {
  const response = await api.post('/payments/initialize', {
    type: 'PLAN',
    planId,
    planType: planType || 'individual',
    upgrade: true,
    subscriptionId
  });
  return response.data;
};

/**
 * Upgrade to a new plan using wallet balance. Subscription must be EXPIRED.
 * @param {string} subscriptionId - Current (expired) subscription ID
 * @param {string} planId - New plan ID
 * @param {string} [planType] - 'individual' or 'family'
 * @returns {Promise<Object>} { success, reference, amount }
 */
export const upgradeWithWallet = async (subscriptionId, planId, planType = 'individual') => {
  const response = await api.post('/payments/wallet/upgrade', {
    subscriptionId,
    planId,
    planType: planType || 'individual'
  });
  return response.data;
};

/**
 * Get payment status by reference
 * @param {string} reference - Payment reference
 * @returns {Promise<Object>} Payment status response
 */
export const getPaymentStatus = async (reference) => {
  try {
    const response = await api.get(`/payments/${reference}`);
    return response.data;
  } catch (error) {
    console.error('Payment status error:', error);
    throw error;
  }
};

export const getPublicPaymentStatus = async (reference) => {
  try {
    const response = await api.get(`/public/payments/${reference}`);
    return response.data;
  } catch (error) {
    console.error('Public payment status error:', error);
    throw error;
  }
};

/**
 * Get payment history for authenticated user
 * @param {Object} params - Query parameters (page, limit)
 * @returns {Promise<Object>} Payment history response with pagination
 */
export const getPaymentHistory = async (params = {}) => {
  try {
    const response = await api.get('/payments', { params });
    return response.data;
  } catch (error) {
    console.error('Payment history error:', error);
    throw error;
  }
};

/**
 * Get wallet balance
 * @returns {Promise<Object>} Wallet balance response
 */
export const getWalletBalance = async () => {
  try {
    const response = await api.get('/user/wallet/balance');
    return response.data;
  } catch (error) {
    console.error('Wallet balance error:', error);
    throw error;
  }
};

/**
 * Get usage summary (percentages per category, no raw amounts) for dashboard
 * @returns {Promise<Object>} { subscription, categories: [{ category, hasLimit, percentUsed, percentRemaining }] }
 */
export const getUsageSummary = async () => {
  try {
    const response = await api.get('/user/usage-summary');
    return response.data;
  } catch (error) {
    console.error('Usage summary error:', error);
    throw error;
  }
};

/**
 * Get wallet transactions
 * @param {Object} params - Query parameters (page, limit, orderBy, type, referenceType)
 * @returns {Promise<Object>} Wallet transactions response with pagination
 */
export const getWalletTransactions = async (params = {}) => {
  try {
    const response = await api.get('/user/wallet/transactions', { params });
    return response.data;
  } catch (error) {
    console.error('Wallet transactions error:', error);
    throw error;
  }
};

/**
 * Get wallet withdrawal requests for the authenticated user
 * @param {Object} params - Query parameters (page, limit, status)
 * @returns {Promise<Object>} Withdrawals response with pagination
 */
export const getWithdrawals = async (params = {}) => {
  try {
    const response = await api.get('/user/withdrawals', { params });
    return response.data;
  } catch (error) {
    console.error('Withdrawals fetch error:', error);
    throw error;
  }
};

/**
 * Get a single wallet withdrawal request by ID
 * @param {string} id - Withdrawal request ID
 * @returns {Promise<Object>} Withdrawal detail response
 */
export const getWithdrawal = async (id) => {
  try {
    const response = await api.get(`/user/withdrawals/${id}`);
    return response.data;
  } catch (error) {
    console.error('Withdrawal fetch error:', error);
    throw error;
  }
};

/**
 * Create a new wallet withdrawal request
 * @param {Object} data
 * @param {number} data.amountNaira - Amount in naira (will be converted to kobo)
 * @param {string} [data.bankName]
 * @param {string} [data.accountNumber]
 * @param {string} [data.accountName]
 * @returns {Promise<Object>} Created withdrawal
 */
export const createWithdrawal = async ({ amountNaira, bankName, accountNumber, accountName }) => {
  try {
    if (!amountNaira || Number.isNaN(Number(amountNaira))) {
      throw new Error('A valid withdrawal amount is required');
    }

    const amountInKobo = Math.round(Number(amountNaira) * 100);

    const payload = {
      amount: amountInKobo
    };

    if (bankName) payload.bankName = bankName;
    if (accountNumber) payload.accountNumber = accountNumber;
    if (accountName) payload.accountName = accountName;

    const response = await api.post('/user/withdrawals', payload);
    return response.data;
  } catch (error) {
    console.error('Create withdrawal error:', error);
    throw error;
  }
};

/**
 * Initialize wallet deposit payment
 * @param {number} amount - Amount in naira
 * @returns {Promise<Object>} Payment initialization response with payment_url
 */
export const initializeWalletDeposit = async (amount) => {
  try {
    return await initializePayment({ type: 'TOPUP', amount });
  } catch (error) {
    console.error('Wallet deposit initialization error:', error);
    throw error;
  }
};

/**
 * Verify email address with token
 * @param {string} token - Email verification token
 * @returns {Promise<Object>} Verification response with verified status
 */
export const verifyEmail = async (token) => {
  try {
    const response = await api.get('/auth/verify-email', {
      params: { token }
    });
    return response.data;
  } catch (error) {
    console.error('Email verification error:', error);
    throw error;
  }
};

/**
 * Resend verification email
 * @param {string} email - User's email address
 * @returns {Promise<Object>} Response with success message
 */
export const resendVerificationEmail = async (email) => {
  try {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  } catch (error) {
    console.error('Resend verification email error:', error);
    throw error;
  }
};

/**
 * Request password reset (forgot password)
 * @param {string} email - User's email address
 * @returns {Promise<Object>} Generic response message
 */
export const forgotPassword = async (email) => {
  try {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    console.error('Forgot password error:', error);
    throw error;
  }
};

/**
 * Verify password reset token
 * @param {string} token - Reset token from email link
 * @returns {Promise<Object>} Response with { valid: boolean, message: string }
 */
export const verifyResetToken = async (token) => {
  try {
    const response = await api.get('/auth/verify-reset-token', {
      params: { token }
    });
    return response.data;
  } catch (error) {
    console.error('Verify reset token error:', error);
    throw error;
  }
};

/**
 * Get announcements for the authenticated user
 * @param {Object} params - Query parameters (type, limit)
 * @returns {Promise<import('axios').AxiosResponse>} Axios response with { announcements }
 */
export const getAnnouncements = (params = {}) => {
  return api.get('/announcements', { params });
};

export const getTransferCheck = async () => {
  const response = await api.get('/transfers/check');
  return response.data;
};

export const searchTransferHospitals = async ({
  query = '',
  excludeId = '',
  state = '',
  lga = ''
}) => {
  const response = await api.get('/hospitals/search', {
    params: { query, excludeId, state, lga }
  });
  return response.data;
};

export const initiateTransfer = async (destinationHospitalId) => {
  const response = await api.post('/transfers/initiate', { destinationHospitalId });
  return response.data;
};

export const getTransferPending = async () => {
  const response = await api.get('/transfers/pending');
  return response.data;
};

export const getTransferHistory = async ({ page = 1, limit = 10 } = {}) => {
  const response = await api.get('/transfers/history', { params: { page, limit } });
  return response.data;
};

export const cancelTransfer = async () => {
  const response = await api.post('/transfers/cancel');
  return response.data;
};

/**
 * Reset password using reset token
 * @param {string} token - Reset token from email link
 * @param {string} newPassword - New password to set
 * @returns {Promise<Object>} Response with success message
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await api.post('/auth/reset-password', {
      token,
      newPassword
    });
    return response.data;
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
};

export const requestAccountDeletion = async (reason) => {
  const payload = {}
  if (typeof reason === 'string' && reason.trim()) {
    payload.reason = reason.trim()
  }
  const response = await api.post('/user/account-deletion-request', payload)
  return response.data
}

export default api

